import type { ReleaseCardData } from "@/components/release-card";
import { releaseCardEditionIdentity } from "@/components/release-card-edition-identity";
import {
  releaseCardVariantModel,
  type ReleaseCardVariant,
} from "@/components/release-card-variant-model";
import {
  buildReleaseFormatDisplay,
  buildReleaseFormatSummaryDisplay,
} from "@/lib/release-format-display";
import { formatCountry } from "@garimpa/lib/countries";

const POLICY_LABELS: Record<string, string> = {
  promotion: "promo",
  unofficial: "non_official_status",
};
const HIDDEN_POLICY_SIGNALS = new Set([
  "explicit_official_status",
  "possibly_official_status",
  "non_official_status",
  "partially_unofficial",
]);
const HIDDEN_RELEASE_STATUSES = new Set(["accepted", "Official"]);

function policyTags(release: ReleaseCardData): string[] {
  const tags = new Set<string>();
  if (release.hasNonOfficialStatus) tags.add("non_official_status");
  if (release.singleMediaFormat === "video") tags.add("single_video_media_format");
  for (const signal of release.releasePolicySignals ?? []) {
    if (HIDDEN_POLICY_SIGNALS.has(signal)) continue;
    tags.add(POLICY_LABELS[signal] ?? signal);
  }
  return [...tags];
}

function formatModel(
  release: ReleaseCardData,
  summaryInput: Parameters<typeof buildReleaseFormatSummaryDisplay>[1],
  detailInput: Parameters<typeof buildReleaseFormatDisplay>[1],
) {
  const summary = release.mediaFormatSummary ?? [];
  const details = release.mediaFormatDetails ?? [];
  const detailTags = release.mediaFormatDetailTags ?? [];
  const hasSummary = summary.length > 0;
  const display = hasSummary
    ? buildReleaseFormatSummaryDisplay(summary, summaryInput)
    : buildReleaseFormatDisplay(details, detailInput);
  return {
    detailTags,
    hasSummary,
    hasFormatPayload: hasSummary || details.length > 0,
    hasDetailText: details.some((detail) => detail.text),
    formatLabel: display.summaryText,
  };
}

function rowModel(release: ReleaseCardData, format: ReturnType<typeof formatModel>) {
  const hasTrackSummary = release.trackCount > 0;
  const hasFormatText = Boolean(release.formatText);
  return {
    hasTrackSummary,
    hasMediaSummaryRow: Boolean(format.formatLabel) || hasTrackSummary || hasFormatText,
    showFormatText: hasFormatText && !format.hasSummary && !format.hasDetailText,
  };
}

function visibleStatus(value: string | null | undefined, extended: boolean): string | null {
  if (!extended || !value || HIDDEN_RELEASE_STATUSES.has(value)) return null;
  return value;
}

export function releaseCardModel(input: {
  detailInput: Parameters<typeof buildReleaseFormatDisplay>[1];
  locale: string;
  release: ReleaseCardData;
  summaryInput: Parameters<typeof buildReleaseFormatSummaryDisplay>[1];
  variant: ReleaseCardVariant;
}) {
  const variant = releaseCardVariantModel(input.variant);
  const format = formatModel(input.release, input.summaryInput, input.detailInput);
  const countryName = input.release.country
    ? formatCountry(input.release.country, input.locale)
    : null;
  const editionIdentity = releaseCardEditionIdentity({
    catnos: input.release.catnos,
    country: countryName,
    date: input.release.date,
    format: format.formatLabel ?? input.release.formatText,
    labelNames: input.release.labelNames,
    title: input.release.title,
  });
  return {
    ...variant,
    ...format,
    ...rowModel(input.release, format),
    countryName,
    editionIdentity,
    policyTags: policyTags(input.release),
    showNotes: variant.extended && Boolean(input.release.notes),
    visibleStatus: visibleStatus(input.release.status, variant.extended),
  };
}

export type ReleaseCardModel = ReturnType<typeof releaseCardModel>;
